import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useApp, useT } from "@/context/AppContext";
import { usePartner } from "@/context/PartnerContext";
import { Trash2, ArrowLeft, Download, CheckCircle2, Truck, Wrench, MapPin } from "lucide-react";
import SubsidyHint from "@/components/SubsidyHint";
import { toast } from "sonner";

export default function CartPage() {
  const t = useT();
  const { cart, removeFromCart, updateQty, clearCart, formatPrice, user, setUser, currency, lang } = useApp();
  const { partner } = usePartner();
  const nav = useNavigate();
  const [step, setStep] = useState("cart");
  const [submitting, setSubmitting] = useState(false);
  const [zones, setZones] = useState([]);
  const [zone, setZone] = useState(null);  // current country's install zone
  const [installMode, setInstallMode] = useState("ship"); // ship | install
  const [createAccount, setCreateAccount] = useState(false);
  const [accountPassword, setAccountPassword] = useState("");
  const [form, setForm] = useState({
    company_name: user?.company || "",
    contact_name: user?.name || "",
    contact_email: user?.email || "",
    contact_phone: "",
    country: "NL",
    address_street: "",
    address_postal: "",
    address_city: "",
    notes: "",
  });
  const [quote, setQuote] = useState(null);
  const [paying, setPaying] = useState(false);
  const [installRates, setInstallRates] = useState([]);
  const [shippingRates, setShippingRates] = useState([]);
  const [installConfig, setInstallConfig] = useState({ benelux_countries: ["NL", "BE", "LU"], install_hour_rate: 100, travel_hour_rate: 60, km_rate: 0.75, default_install_hours: 1 });
  const [installDuration, setInstallDuration] = useState("full_day");
  const [installHours, setInstallHours] = useState(1);
  const [travelHours, setTravelHours] = useState(0);
  const [installKm, setInstallKm] = useState(0);

  const INSTALL_DURATIONS = [
    { key: "hour1", label: "1 uur (incl. reiskosten)", field: "hour1_travel" },
    { key: "half_day", label: "Halve dag (4u)", field: "half_day" },
    { key: "full_day", label: "Hele dag (8u)", field: "full_day" },
  ];

  const DEPOSIT_THRESHOLD = 10000;
  const paymentPlan = useMemo(() => {
    const total = quote?.total_once || 0;
    if (total > DEPOSIT_THRESHOLD) return { plan: "deposit", amount: Math.round(total * 0.5 * 100) / 100 };
    return { plan: "full", amount: Math.round(total * 100) / 100 };
  }, [quote]);

  const startPayment = async () => {
    if (!quote) return;
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout/quote", {
        quote_id: quote.id,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Kon de betaling niet starten");
        setPaying(false);
      }
    } catch (e) {
      toast.error("Betaling starten mislukt: " + (e.response?.data?.detail || e.message));
      setPaying(false);
    }
  };

  // Load install zones once
  useEffect(() => {
    api.get("/install-zones").then((r) => setZones(r.data || [])).catch(() => {});
    api.get("/installation/rates").then((r) => setInstallRates(r.data || [])).catch(() => {});
    api.get("/shipping/rates").then((r) => setShippingRates(r.data || [])).catch(() => {});
    api.get("/installation/config").then((r) => r.data && setInstallConfig(r.data)).catch(() => {});
  }, []);

  // Pick zone when country changes
  useEffect(() => {
    const z = zones.find((x) => x.country === (form.country || "").toUpperCase());
    setZone(z || null);
  }, [form.country, zones]);

  const cc = (form.country || "").toUpperCase();
  const isBenelux = (installConfig.benelux_countries || []).includes(cc);
  const installRate = useMemo(
    () => installRates.find((r) => r.country_code === cc) || null,
    [installRates, cc]
  );
  const shippingRate = useMemo(
    () => shippingRates.find((r) => r.country_code === cc) || null,
    [shippingRates, cc]
  );
  const installFee = useMemo(() => {
    if (isBenelux) {
      return (Number(installHours) || 0) * (installConfig.install_hour_rate || 100)
        + (Number(travelHours) || 0) * (installConfig.travel_hour_rate || 60)
        + (Number(installKm) || 0) * (installConfig.km_rate || 0.75);
    }
    if (!installRate) return 0;
    const field = INSTALL_DURATIONS.find((d) => d.key === installDuration)?.field || "full_day";
    return installRate[field] || 0;
  }, [isBenelux, installHours, travelHours, installKm, installConfig, installRate, installDuration]);
  const shippingFee = shippingRate ? shippingRate.price : 0;

  const totals = useMemo(() => {
    let once = 0; let monthly = 0;
    cart.forEach((it) => {
      const q = it.quantity || 1;
      once += (it.unit_price || 0) * q;
      monthly += (it.monthly_price || 0) * q;
    });
    const extraFee = step === "checkout" ? (installMode === "install" ? installFee : shippingFee) : 0;
    return { once, monthly, extraFee, onceTotal: once + extraFee };
  }, [cart, step, installMode, installFee, shippingFee]);

  const submit = async () => {
    if (!form.company_name || !form.contact_name || !form.contact_email) {
      toast.error("Vul alle vereiste velden in");
      return;
    }
    if (createAccount && (!accountPassword || accountPassword.length < 6)) {
      toast.error("Wachtwoord minimaal 6 tekens");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: cart,
        currency,
        language: lang,
        ...form,
        install_mode: installMode,
        install_country: form.country || null,
        install_duration: installDuration,
        install_hours: isBenelux ? Number(installHours) || 0 : null,
        travel_hours: isBenelux ? Number(travelHours) || 0 : null,
        install_km: isBenelux ? Number(installKm) || 0 : null,
        install_fee: installFee,
        shipping_fee: shippingFee,
        partner_slug: partner?.slug || null,
        create_account: createAccount && !user,
        account_password: createAccount && !user ? accountPassword : undefined,
      };
      const { data } = await api.post("/quotes", payload);
      setQuote(data);
      setStep("done");
      clearCart();
      if (data.account?.user_id) {
        // Auto-fetch the new user so the header shows the logged-in state
        try {
          const me = await api.get("/auth/me");
          setUser(me.data);
        } catch { /* ignore */ }
        toast.success("Offerte ontvangen én account aangemaakt, je bent ingelogd.");
      } else {
        toast.success(t.checkout.success);
      }
    } catch (e) {
      toast.error("Er ging iets mis, probeer opnieuw");
    } finally {
      setSubmitting(false);
    }
  };

  if (step === "done" && quote) {
    return (
      <div className="container-x py-24 max-w-2xl mx-auto text-center" data-testid="checkout-success-page">
        <div className="w-20 h-20 mx-auto rounded-full bg-[#1F3A2E] grid place-items-center">
          <CheckCircle2 className="w-10 h-10 text-[#FDFBF7]" />
        </div>
        <h1 className="display text-3xl md:text-4xl font-semibold mt-6">{t.checkout.success}</h1>
        <p className="text-[#A8B3AD] mt-3">Offertenummer <b>{quote.quote_number}</b></p>

        {quote.total_once > 0 && (
          <div className="card-surface p-6 mt-8 text-left max-w-md mx-auto" data-testid="payment-cta-block">
            <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#6FAA8A] mb-3">Direct betalen</div>
            <div className="flex justify-between text-sm">
              <span className="text-[#A8B3AD]">Totaalbedrag offerte</span>
              <span className="font-semibold">{formatPrice(quote.total_once)}</span>
            </div>
            {paymentPlan.plan === "deposit" ? (
              <>
                <div className="flex justify-between text-sm mt-2">
                  <span className="text-[#A8B3AD]">Aanbetaling (50%)</span>
                  <span className="font-bold text-[#6FAA8A]">{formatPrice(paymentPlan.amount)}</span>
                </div>
                <p className="text-[11px] text-[#A8B3AD] mt-2">Bij bedragen boven {formatPrice(DEPOSIT_THRESHOLD)} betaal je nu 50% aanbetaling; het restant volgt later.</p>
              </>
            ) : (
              <div className="flex justify-between text-sm mt-2">
                <span className="text-[#A8B3AD]">Nu te betalen</span>
                <span className="font-bold text-[#6FAA8A]">{formatPrice(paymentPlan.amount)}</span>
              </div>
            )}
            <button className="btn-accent w-full justify-center mt-5" onClick={startPayment} disabled={paying} data-testid="pay-now-button">
              {paying ? "Bezig…" : `Betaal ${formatPrice(paymentPlan.amount)}`}
            </button>
          </div>
        )}

        <div className="mt-8 flex gap-3 justify-center">
          <a href={`${API}/quotes/${quote.id}/pdf`} target="_blank" rel="noreferrer" className="btn-primary" data-testid="download-pdf-button">
            <Download className="w-4 h-4" /> {t.checkout.download}
          </a>
          <button className="btn-outline" onClick={() => nav("/")} data-testid="back-home-button">
            <ArrowLeft className="w-4 h-4" /> Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-x py-12" data-testid="cart-page">
      <h1 className="display font-semibold text-3xl md:text-5xl text-[#F4F1EA]">{step === "cart" ? t.cart.title : t.checkout.title}</h1>

      {cart.length === 0 && step === "cart" && (
        <div className="card-surface p-10 mt-10 text-center">
          <div className="text-lg text-[#A8B3AD]">{t.cart.empty}</div>
          <button className="btn-primary mt-6" onClick={() => nav("/configurator")} data-testid="empty-cart-cta">
            {t.home.cta_start}
          </button>
        </div>
      )}

      {(cart.length > 0 || step === "checkout") && (
        <div className="grid lg:grid-cols-3 gap-8 mt-10">
          <div className="lg:col-span-2 space-y-3">
            {step === "cart" && cart.map((it, i) => (
              <div key={i} className="card-surface p-4 flex items-center gap-4" data-testid={`cart-item-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#F4F1EA] truncate">{it.name}</div>
                  {it.brand && (
                    <div className="text-[11px] text-[#7A8580] truncate" title={it.brand} data-testid={`cart-item-${i}-brand`}>{it.brand}</div>
                  )}
                  <div className="text-xs text-[#A8B3AD] mt-0.5">{it.sku || ""} · {it.type} · {it.pricing_mode}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="btn-ghost !p-2" onClick={() => updateQty(i, (it.quantity || 1) - 1)} data-testid={`qty-minus-${i}`}>−</button>
                  <div className="w-8 text-center font-semibold">{it.quantity || 1}</div>
                  <button className="btn-ghost !p-2" onClick={() => updateQty(i, (it.quantity || 1) + 1)} data-testid={`qty-plus-${i}`}>+</button>
                </div>
                <div className="text-right min-w-[120px]">
                  {it.unit_price ? <div className="font-semibold text-[#6FAA8A]">{formatPrice(it.unit_price * (it.quantity || 1))}</div> : null}
                  {it.monthly_price ? <div className="text-xs text-[#D97742]">{formatPrice(it.monthly_price * (it.quantity || 1))}/mnd</div> : null}
                </div>
                <button className="btn-ghost !p-2 text-[#D97742]" onClick={() => removeFromCart(i)} data-testid={`remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}

            {step === "checkout" && (
              <form className="card-surface p-6 space-y-5" onSubmit={(e) => { e.preventDefault(); submit(); }} data-testid="checkout-form">
                <Field label={t.checkout.company} value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} id="checkout-company" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label={t.checkout.name} value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} id="checkout-name" />
                  <Field label={t.checkout.email} type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} id="checkout-email" />
                </div>
                <Field label={t.checkout.phone} value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} id="checkout-phone" required={false} />

                {/* ----- Delivery address block ----- */}
                <div className="pt-2 border-t border-white/5">
                  <div className="flex items-center gap-2 text-xs font-semibold text-[#6FAA8A] uppercase tracking-wider mt-3 mb-2">
                    <MapPin className="w-3.5 h-3.5" /> Leveradres
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <label className="block sm:col-span-2">
                      <span className="text-xs text-[#A8B3AD]">Straat + nummer</span>
                      <input className="input-field mt-1" value={form.address_street} onChange={(e) => setForm({ ...form, address_street: e.target.value })} data-testid="checkout-street" />
                    </label>
                    <label className="block">
                      <span className="text-xs text-[#A8B3AD]">Postcode</span>
                      <input className="input-field mt-1" value={form.address_postal} onChange={(e) => setForm({ ...form, address_postal: e.target.value })} data-testid="checkout-postal" />
                    </label>
                    <label className="block">
                      <span className="text-xs text-[#A8B3AD]">Plaats</span>
                      <input className="input-field mt-1" value={form.address_city} onChange={(e) => setForm({ ...form, address_city: e.target.value })} data-testid="checkout-city" />
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="text-xs text-[#A8B3AD]">Land</span>
                      <select className="input-field mt-1" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="checkout-country">
                        {zones.map((z) => (
                          <option key={z.country} value={z.country}>{z.country_name_nl} ({z.country})</option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>

                {/* ----- Install vs Ship toggle ----- */}
                {cart.length > 0 && (
                  <div className="pt-2">
                    <div className="text-xs font-semibold text-[#6FAA8A] uppercase tracking-wider mb-2">Installatie</div>
                    <div className="grid sm:grid-cols-2 gap-3" data-testid="install-mode-toggle">
                      <button type="button" onClick={() => setInstallMode("ship")}
                        className={`p-4 rounded-xl border text-left transition-all ${installMode === "ship" ? "border-[#6FAA8A] bg-[#6FAA8A]/10" : "border-white/10 hover:border-white/20"}`}
                        data-testid="install-mode-ship">
                        <div className="flex items-center gap-2">
                          <Truck className="w-4 h-4 text-[#6FAA8A]" />
                          <div className="font-semibold text-[#F4F1EA]">Zelf installeren</div>
                        </div>
                        <div className="text-xs text-[#A8B3AD] mt-2">Wij sturen je kiosk op, plug &amp; play binnen 20 min.</div>
                        <div className="mt-3 text-[#D97742] font-semibold" data-testid="shipping-fee-display">{shippingRate ? `${formatPrice(shippingFee)} verzendkosten` : "Verzendkosten op aanvraag"}</div>
                      </button>
                      <button type="button" onClick={() => setInstallMode("install")}
                        className={`p-4 rounded-xl border text-left transition-all ${installMode === "install" ? "border-[#D97742] bg-[#D97742]/10" : "border-white/10 hover:border-white/20"}`}
                        data-testid="install-mode-install">
                        <div className="flex items-center gap-2">
                          <Wrench className="w-4 h-4 text-[#D97742]" />
                          <div className="font-semibold text-[#F4F1EA]">Laten installeren</div>
                        </div>
                        <div className="text-xs text-[#A8B3AD] mt-2">Wij komen jouw kiosk plaatsen. Volledig ontzorgd.</div>
                        <div className="mt-3 text-[#D97742] font-semibold" data-testid="install-fee-display">
                          {(isBenelux || installRate) ? `${formatPrice(installFee)} installatiekosten` : "Prijs op aanvraag"}
                        </div>
                      </button>
                    </div>
                    {installMode === "install" && (
                      <div className="mt-3" data-testid="install-duration-block">
                        {isBenelux ? (
                          <div className="grid sm:grid-cols-3 gap-3">
                            <label className="block">
                              <span className="text-xs font-semibold text-[#6FAA8A] uppercase tracking-wider">Installatie-uren</span>
                              <input type="number" min="0" step="0.5" className="input-field mt-1" value={installHours}
                                onChange={(e) => setInstallHours(e.target.value)} data-testid="install-hours-input" />
                              <span className="text-[10px] text-[#A8B3AD]">€ {installConfig.install_hour_rate}/uur</span>
                            </label>
                            <label className="block">
                              <span className="text-xs font-semibold text-[#6FAA8A] uppercase tracking-wider">Reisuren</span>
                              <input type="number" min="0" step="0.5" className="input-field mt-1" value={travelHours}
                                onChange={(e) => setTravelHours(e.target.value)} data-testid="travel-hours-input" />
                              <span className="text-[10px] text-[#A8B3AD]">€ {installConfig.travel_hour_rate}/uur</span>
                            </label>
                            <label className="block">
                              <span className="text-xs font-semibold text-[#6FAA8A] uppercase tracking-wider">Kilometers</span>
                              <input type="number" min="0" step="1" className="input-field mt-1" value={installKm}
                                onChange={(e) => setInstallKm(e.target.value)} data-testid="install-km-input" />
                              <span className="text-[10px] text-[#A8B3AD]">€ {installConfig.km_rate}/km</span>
                            </label>
                            <div className="sm:col-span-3 text-[11px] text-[#A8B3AD]">Benelux: eigen installatie (arbeid + reistijd + km). Reisuren/km worden bepaald op basis van de locatie.</div>
                          </div>
                        ) : installRate ? (
                          <>
                            <label className="block">
                              <span className="text-xs font-semibold text-[#6FAA8A] uppercase tracking-wider">Duur installatie ({installRate.country_name})</span>
                              <select className="input-field mt-1" value={installDuration}
                                onChange={(e) => setInstallDuration(e.target.value)} data-testid="install-duration-select">
                                {INSTALL_DURATIONS.map((d) => (
                                  <option key={d.key} value={d.key}>{d.label} · {formatPrice(installRate[d.field] || 0)}</option>
                                ))}
                              </select>
                            </label>
                            {installRate.capital_only && <div className="mt-1 text-[11px] text-[#D97742]">Alleen in de hoofdstad en directe omgeving; andere steden op aanvraag.</div>}
                            {installRate.note && <div className="mt-1 text-[11px] text-[#A8B3AD]">{installRate.note}</div>}
                            <div className="mt-1 text-[11px] text-[#A8B3AD]">Tarief incl. reiskosten; exacte reistijd/km-toeslag kan later worden bepaald.</div>
                          </>
                        ) : (
                          <div className="text-[11px] text-[#D97742]" data-testid="install-on-request">Voor dit land maken we een installatie-offerte op maat. Het bedrag wordt later toegevoegd.</div>
                        )}
                      </div>
                    )}
                    <div className="mt-2 text-[11px] text-[#A8B3AD]">Levertijd {zone.lead_time_days} werkdagen · partner: {zone.install_partner}</div>
                  </div>
                )}

                <label className="block pt-2">
                  <span className="text-xs font-semibold text-[#6FAA8A] uppercase tracking-wider">{t.checkout.notes}</span>
                  <textarea className="input-field mt-1" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="checkout-notes" />
                </label>

                {/* ----- Optional: create account ----- */}
                {!user && (
                  <div className="pt-3 border-t border-white/5" data-testid="create-account-block">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={createAccount}
                        onChange={(e) => setCreateAccount(e.target.checked)}
                        className="mt-1 w-4 h-4 rounded accent-[#6FAA8A]"
                        data-testid="checkout-create-account"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-[#F4F1EA]">Maak direct een account aan</div>
                        <div className="text-xs text-[#A8B3AD] mt-0.5">
                          Log later in om je offerte aan te passen, opnieuw te bestellen of de status te volgen.
                        </div>
                      </div>
                    </label>
                    {createAccount && (
                      <label className="block mt-3" data-testid="checkout-password-wrap">
                        <span className="text-xs text-[#A8B3AD]">Wachtwoord (min. 6 tekens)</span>
                        <input
                          type="password"
                          className="input-field mt-1"
                          value={accountPassword}
                          onChange={(e) => setAccountPassword(e.target.value)}
                          minLength={6}
                          data-testid="checkout-password"
                        />
                      </label>
                    )}
                  </div>
                )}

                <button type="submit" className="btn-accent w-full justify-center" disabled={submitting} data-testid="checkout-submit-button">
                  {submitting ? "Versturen…" : t.checkout.submit}
                </button>
              </form>
            )}
          </div>

          <aside className="card-surface p-6 h-fit sticky top-28" data-testid="cart-summary">
            <div className="overline text-[10px] uppercase tracking-[0.2em] font-bold text-[#6FAA8A] mb-3">Overzicht</div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm"><span>Hardware + software</span><span className="font-semibold">{formatPrice(totals.once)}</span></div>
              {totals.monthly > 0 && <div className="flex justify-between text-sm"><span>{t.cart.monthly}</span><span className="font-semibold">{formatPrice(totals.monthly)}</span></div>}
              {step === "checkout" && (
                <div className="flex justify-between text-sm border-t border-white/5 pt-2 mt-2">
                  <span className="text-[#A8B3AD]">{installMode === "install" ? "Installatie" : "Verzending"}</span>
                  <span className="font-semibold text-[#D97742]">{formatPrice(totals.extraFee)}</span>
                </div>
              )}
              <div className="flex justify-between text-base border-t border-white/10 pt-3 mt-3">
                <span className="font-semibold">{t.cart.one_time}</span>
                <span className="font-bold text-[#6FAA8A]">{formatPrice(totals.onceTotal)}</span>
              </div>
            </div>
            {totals.onceTotal > 0 && (
              <div className="mt-4">
                <SubsidyHint amount={totals.onceTotal} variant="banner" />
              </div>
            )}
            {step === "cart" ? (
              <button className="btn-accent w-full justify-center mt-6" onClick={() => setStep("checkout")} disabled={cart.length === 0} data-testid="goto-checkout-button">
                {t.cart.checkout}
              </button>
            ) : (
              <button className="btn-ghost w-full justify-center mt-6" onClick={() => setStep("cart")} data-testid="back-to-cart-button">
                <ArrowLeft className="w-4 h-4" /> Terug
              </button>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", id, required = true }) => (
  <label className="block">
    <span className="text-xs font-semibold text-[#6FAA8A] uppercase tracking-wider">{label}</span>
    <input className="input-field mt-1" value={value} type={type} onChange={(e) => onChange(e.target.value)} data-testid={id} required={required} />
  </label>
);
